import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type { EnrichedPart, LifecycleStatus } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import { getOutcomeRouting, type RuleOutcomeKey, type RuleOutcomeRouting } from "../../../rules/ruleLogicDefinitions";
import type { RuleIntegrationEvaluationContext } from "../../ruleIntegrationTypes";
import {
    runStatementRuleBoolean,
} from "../../executableFamilies/statementRule/StatementRuleIntegrationAdapter";
import {
    buildBlockedValuesExpression,
    createStatementRuleSimplifiedLogicBridge,
    getBlockedValuesCondition,
    type BlockedValuesBridgeConfig,
} from "../../statementRuleSimplifiedLogic";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { StatementRuleIntegrationPolicy } from "../statementRulePolicyTypes";

export const DQ003_RULE_ID = "DQ-003";
export const DQ003_APPGLUE_EXECUTABLE_TYPE = "StatementRuleConfiguration";
export const DQ003_OUTPUT_QUEUE: DemoOutputQueue = "archived_parts";
export const DQ003_BLOCKED_LIFECYCLE_STATUSES = ["EOL", "NRND"] as const;
export const DQ003_LIFECYCLE_STATUS_OPTIONS: LifecycleStatus[] = ["EOL", "NRND", "Preliminary", "Active"];
export const DQ003_LIFECYCLE_FIELD_OPTIONS = [
    { value: "lifecycle_status", label: "lifecycle_status" },
    { value: "prior_lifecycle_status", label: "prior_lifecycle_status" },
] as const;

export type Dq003BlockedLifecycleStatus = (typeof DQ003_BLOCKED_LIFECYCLE_STATUSES)[number];
export type Dq003LifecycleField = (typeof DQ003_LIFECYCLE_FIELD_OPTIONS)[number]["value"];

export type Dq003LifecycleStatusEvidence = {
    status: DemoRuleStatus;
    lifecycleStatus: LifecycleStatus;
    lifecycleField: Dq003LifecycleField;
    blockedLifecycleStatuses: LifecycleStatus[];
    appGlueRuntimeError?: string;
    unsupportedNativeExpression?: boolean;
    outputQueue?: DemoOutputQueue;
};

const DEFAULT_DQ003_OUTCOME_ROUTING: Record<RuleOutcomeKey, RuleOutcomeRouting> = {
    pass: {
        id: "dq003-pass-routing",
        when: "pass",
        status: "pass",
        label: "allow active catalog publish",
        outputQueue: "published_catalog_candidates",
        reasonCode: "LIFECYCLE_ALLOWED",
        explanation: "Lifecycle status is allowed for active catalog publish.",
    },
    fail: {
        id: "dq003-fail-routing",
        when: "fail",
        status: "rejected",
        label: "archive part",
        outputQueue: DQ003_OUTPUT_QUEUE,
        reasonCode: "LIFECYCLE_BLOCKED",
        explanation: "Lifecycle status blocks active catalog publish and routes the part to archive.",
    },
    error: {
        id: "dq003-error-routing",
        when: "error",
        status: "rejected",
        label: "hold incomplete expression",
        outputQueue: "pending_review",
        reasonCode: "APPGLUE_EXPRESSION_INCOMPLETE",
        explanation:
            "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed.",
    },
};

const getDq003OutcomeRouting = (
    context: RuleIntegrationEvaluationContext | undefined,
    when: RuleOutcomeKey,
): RuleOutcomeRouting => getOutcomeRouting(context?.definition, when) ?? DEFAULT_DQ003_OUTCOME_ROUTING[when];

const DQ003_SIMPLIFIED_LOGIC_CONFIG: BlockedValuesBridgeConfig = {
    mode: "blocked_values",
    conditionId: "dq003-lifecycle-blocked-statuses",
    conditionFieldOptions: [...DQ003_LIFECYCLE_FIELD_OPTIONS],
    conditionValueOptions: DQ003_LIFECYCLE_STATUS_OPTIONS.map((status) => ({
        value: status,
        label: status,
    })),
    conditionValueInputLabel: "blocked lifecycle statuses",
    defaultField: "lifecycle_status",
    defaultValues: [...DQ003_BLOCKED_LIFECYCLE_STATUSES],
    actionFor: (state, context) => getDq003OutcomeRouting(context, state.values.length > 0 ? "fail" : "pass").label,
};
const DQ003_SIMPLIFIED_LOGIC_BRIDGE = createStatementRuleSimplifiedLogicBridge(DQ003_SIMPLIFIED_LOGIC_CONFIG);

const createDq003InputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    inputData.fields.push(new StringDataDefinition("lifecycle_status"));
    inputData.fields.push(new StringDataDefinition("prior_lifecycle_status"));
    return inputData;
};

export const buildDq003ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData({
        lifecycle_status: part.lifecycleStatus,
        prior_lifecycle_status: part.priorLifecycleStatus,
    });

export const createDq003StatementRuleConfiguration = (): StatementRuleConfiguration => {
    AppGlueCoreInit.init();

    const config = new StatementRuleConfiguration();
    config.title = "DQ-003 Lifecycle status gating";
    config.inputData = createDq003InputDefinition();
    config.executionResult = new BooleanDataDefinition();

    config.expression.expression = buildBlockedValuesExpression({
        field: "lifecycle_status",
        values: [...DQ003_BLOCKED_LIFECYCLE_STATUSES],
    });

    return config;
};

type Dq003LifecycleCondition = {
    field: Dq003LifecycleField;
    values: LifecycleStatus[];
};

const getDq003LifecycleFieldValue = (part: EnrichedPart, field: Dq003LifecycleField): LifecycleStatus =>
    field === "prior_lifecycle_status" ? part.priorLifecycleStatus : part.lifecycleStatus;

const getConfiguredDq003LifecycleCondition = (
    config: StatementRuleConfiguration,
): Dq003LifecycleCondition | null => {
    const condition = getBlockedValuesCondition(config, DQ003_SIMPLIFIED_LOGIC_CONFIG);
    if (!condition) return null;

    return {
        field: condition.field as Dq003LifecycleField,
        values: condition.values as LifecycleStatus[],
    };
};

const evaluateDq003Part = (
    config: StatementRuleConfiguration,
    part: EnrichedPart,
    context?: RuleIntegrationEvaluationContext,
) => {
    const { appGluePassed, appGlueRuntimeError } = runStatementRuleBoolean(config, buildDq003ExecutionData(part));
    const lifecycleCondition = getConfiguredDq003LifecycleCondition(config);
    const routing = getDq003OutcomeRouting(context, appGlueRuntimeError ? "error" : appGluePassed ? "pass" : "fail");
    if (!appGlueRuntimeError && !lifecycleCondition) {
        return {
            appGluePassed,
            routing,
            evidence: {
                status: routing.status,
                lifecycleStatus: part.lifecycleStatus,
                lifecycleField: "lifecycle_status",
                blockedLifecycleStatuses: [],
                unsupportedNativeExpression: true,
                outputQueue: routing.outputQueue,
            } satisfies Dq003LifecycleStatusEvidence,
        };
    }

    const characterizedLifecycleCondition = lifecycleCondition ?? {
        field: "lifecycle_status" as Dq003LifecycleField,
        values: [...DQ003_BLOCKED_LIFECYCLE_STATUSES],
    };
    const status: DemoRuleStatus = routing.status;

    return {
        appGluePassed,
        routing,
        evidence: {
            status,
            lifecycleStatus: getDq003LifecycleFieldValue(part, characterizedLifecycleCondition.field),
            lifecycleField: characterizedLifecycleCondition.field,
            blockedLifecycleStatuses: characterizedLifecycleCondition.values,
            appGlueRuntimeError,
            outputQueue: routing.outputQueue,
        } satisfies Dq003LifecycleStatusEvidence,
    };
};

export const DQ003_STATEMENT_RULE_POLICY: StatementRuleIntegrationPolicy<EnrichedPart> = {
    ruleId: DQ003_RULE_ID,
    policyBinding: requireRulePolicyBinding(DQ003_RULE_ID),
    createConfig: createDq003StatementRuleConfiguration,
    evaluator: {
        evaluate: (config, part, context) => {
            const { appGluePassed, evidence, routing } = evaluateDq003Part(config, part, context);

            return {
                passed: appGluePassed,
                status: evidence.status,
                reasonCode: evidence.unsupportedNativeExpression
                    ? evidence.status === "pass"
                        ? "APPGLUE_NATIVE_EXPRESSION_PASSED"
                        : "APPGLUE_NATIVE_EXPRESSION_REJECTED"
                    : routing.reasonCode,
                explanation: evidence.unsupportedNativeExpression
                    ? "The native AppGlue expression evaluated this row, but its shape is outside the simplified lifecycle evidence model. Use the native editor for full logic."
                    : routing.explanation,
                outputQueue: routing.outputQueue,
                comparisons: evidence.appGlueRuntimeError
                    ? [
                          {
                              label: "AppGlue expression",
                              expected: "complete executable expression",
                              actual: evidence.appGlueRuntimeError,
                              ok: false,
                          },
                      ]
                    : evidence.unsupportedNativeExpression
                        ? [
                              {
                                  label: "AppGlue expression",
                                  expected: "row passes native expression",
                                  actual: appGluePassed ? "passed" : "rejected",
                                  ok: appGluePassed,
                              },
                          ]
                    : [
                          {
                              label: "Lifecycle",
                              expected: `not ${evidence.blockedLifecycleStatuses.join(" or ")}`,
                              actual: evidence.lifecycleStatus,
                              ok: evidence.status !== "rejected",
                          },
                      ],
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: DQ003_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: DQ003_SIMPLIFIED_LOGIC_BRIDGE.summaryPresenter,
    simplifiedLogicEditor: DQ003_SIMPLIFIED_LOGIC_BRIDGE.simplifiedLogicEditor,
    runtimeSamples: {
        get passingRow() {
            return DQ003_RUNTIME_SAMPLES.passingRow;
        },
        get routedRow() {
            return DQ003_RUNTIME_SAMPLES.routedRow;
        },
    },
};

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-DQ003-SAMPLE",
    mpn: "TI-0001SEM",
    manufacturerId: "MFR-001",
    manufacturerName: "Texas Instruments",
    category: "Semiconductor",
    taxonomyPath: "Semiconductor > Logic",
    packageType: "QFN",
    pinCount: 32,
    voltageMin: 1.8,
    voltageMax: 3.3,
    capacitancePf: null,
    resistanceOhms: null,
    tolerancePct: 5,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 95,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

export const DQ003_RUNTIME_SAMPLES = {
    passingRow: samplePart(),
    routedRow: samplePart({
        partId: "PART-DQ003-ROUTED",
        lifecycleStatus: "EOL",
        priorLifecycleStatus: "Active",
        recommendedReplacementMpn: "TI-0001SEM-ALT",
    }),
};
